/* eslint-disable react/display-name */
import { useInspectorContext } from "@app-context/inspector/context";
import { OEMS_INITIALIZATION_STEPS, useOEMS } from "@app-context/oems/context";
import { useEverTrue, useRefCallback } from "@enfusion-ui/hooks";
import {
  Button,
  CenterContent,
  ContentMessage,
} from "@enfusion-ui/web-components";
import { styled } from "@enfusion-ui/web-core";
import { faSkullCrossbones } from "@fortawesome/free-solid-svg-icons";
import * as React from "react";

const ContentProgressContainer = styled.div`
  color: var(--primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
`;

const ContentProgressText = styled.div`
  font-size: 0.75em;
  font-weight: bold;
  margin-top: 8px;
`;

export type ContentProgressProps = {
  value: number;
  total: number;
  message: string;
};
const ProgressBarContainer = styled.div`
  border: 2px solid;
  border-color: var(--primary);
  border-radius: 16px;
  padding: 2px;
  width: 192px;
`;

const ProgressBarFill = styled.div`
  background-color: var(--primary);
  height: 8px;
  border-radius: 16px;
  transition: width 300ms;
`;

export type Props = {
  value: number;
  total: number;
};

function ProgressBar({ value, total }: Props) {
  return (
    <ProgressBarContainer>
      <ProgressBarFill
        style={{ width: `${(value / total) * 100}%` }}
        data-testid="fill"
      />
    </ProgressBarContainer>
  );
}

function ContentProgress({ value, total, message }: ContentProgressProps) {
  return (
    <ContentProgressContainer>
      <ProgressBar value={value} total={total} />
      <ContentProgressText>{message}</ContentProgressText>
    </ContentProgressContainer>
  );
}
// Handle OMS init status
const OMSContentView: React.FC = ({ children }) => {
  const {
    hasError,
    errorMessage,
    restartSocket,
    isInitialized,
    initializationStep,
    initializationMessage,
  } = useOEMS();
  const { getSecureToken } = useInspectorContext();

  const handleRestartSocket = useRefCallback(async () => {
    const secureToken = await getSecureToken();
    restartSocket(secureToken);
  }, [restartSocket]);

  const everInitialized = useEverTrue(isInitialized);

  if (hasError) {
    return (
      <CenterContent>
        <ContentMessage
          icon={faSkullCrossbones}
          message={errorMessage ?? "There was an OEMS connection issue."}
          containerStyle={{
            maxWidth: "50%",
          }}
        >
          <Button primary onClick={handleRestartSocket}>
            Retry Connection
          </Button>
        </ContentMessage>
      </CenterContent>
    );
  }

  if (!everInitialized) {
    return (
      <CenterContent>
        <ContentProgress
          value={
            initializationMessage === "OMS Initialized"
              ? OEMS_INITIALIZATION_STEPS
              : initializationStep
          }
          total={Math.max(OEMS_INITIALIZATION_STEPS, initializationStep)}
          message={initializationMessage || "Opening Socket"}
        />
      </CenterContent>
    );
  }

  return <>{children}</>;
};

export default OMSContentView;
